import { Home, Receipt, Rocket, User, Camera } from 'lucide-react';
import type { MobileTab } from '../types/index';

interface MobileTabBarProps {
  current: MobileTab;
  onChange: (tab: MobileTab) => void;
  onScan: () => void;
}

const LEFT_TABS = [
  { id: 'home' as MobileTab, icon: Home, label: 'Início' },
  { id: 'lancamentos' as MobileTab, icon: Receipt, label: 'Lançamentos' },
] as const;

const RIGHT_TABS = [
  { id: 'prestacao' as MobileTab, icon: Rocket, label: 'Prestação' },
  { id: 'eu' as MobileTab, icon: User, label: 'Eu' },
] as const;

function isActive(current: MobileTab, id: MobileTab): boolean {
  return current === id;
}

/**
 * Barra de navegação mobile — 5 colunas com FAB central elevado.
 * FAB usa gradiente clay conforme decisão D1 do SDD.
 * Touch targets >= 44px em todos os itens.
 */
export function MobileTabBar({ current, onChange, onScan }: MobileTabBarProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 84px 1fr 1fr',
        alignItems: 'center',
        borderTop: '1px solid var(--card-stroke)',
        background: 'var(--frame)',
        padding: '6px 10px 24px',
        position: 'relative',
        flexShrink: 0,
      }}
      role="navigation"
      aria-label="Navegação principal"
    >
      {LEFT_TABS.map(({ id, icon: Icon, label }) => {
        const active = isActive(current, id);
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              padding: '6px 0',
              minHeight: 44,
              color: active ? 'var(--mint-deep)' : 'var(--fg-dim)',
              background: 'none',
              border: 0,
              cursor: 'pointer',
              font: 'inherit',
              transition: 'color 120ms',
            }}
          >
            <Icon size={21} strokeWidth={active ? 2 : 1.6} />
            <span style={{ fontSize: 10, fontWeight: active ? 700 : 500 }}>{label}</span>
          </button>
        );
      })}

      {/* FAB central — clay gradient (D1) */}
      <div style={{ display: 'grid', placeItems: 'center' }}>
        <button
          onClick={onScan}
          aria-label="Escanear documento"
          style={{
            width: 60,
            height: 60,
            minWidth: 60,
            borderRadius: '50%',
            background: 'linear-gradient(160deg, var(--clay) 0%, var(--clay-deep) 100%)',
            color: 'var(--on-clay)',
            display: 'grid',
            placeItems: 'center',
            marginTop: -30,
            border: '4px solid var(--frame)',
            boxShadow:
              '0 12px 24px -6px rgba(184,120,80,0.55), inset 0 1px 0 rgba(255,255,255,0.35)',
            cursor: 'pointer',
            transition: 'transform 120ms, filter 120ms',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)';
            (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1.05)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
            (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1)';
          }}
        >
          <Camera size={26} strokeWidth={1.8} />
        </button>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--clay-deep)',
            marginTop: 2,
            userSelect: 'none',
          }}
        >
          Escanear
        </span>
      </div>

      {RIGHT_TABS.map(({ id, icon: Icon, label }) => {
        const active = isActive(current, id);
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              padding: '6px 0',
              minHeight: 44,
              color: active ? 'var(--mint-deep)' : 'var(--fg-dim)',
              background: 'none',
              border: 0,
              cursor: 'pointer',
              font: 'inherit',
              transition: 'color 120ms',
            }}
          >
            <Icon size={21} strokeWidth={active ? 2 : 1.6} />
            <span style={{ fontSize: 10, fontWeight: active ? 700 : 500 }}>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
